import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  CircularProgress,
  Button,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Collapse,
  Tooltip,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import PrintIcon from '@mui/icons-material/Print';
import { useSnackbar } from 'notistack';
import cuentaCorrienteService, {
  CuentaCorrienteComprobante,
  CuentaCorrienteSaldo,
  MovimientoHistorico
} from '../../services/cuentaCorrienteService';
import clienteService from '../../services/clienteService';
import { formatCurrency } from '../../utils/formatters';
import * as XLSX from 'xlsx';
import ClienteSearch from '../../components/Tesoreria/ClienteSearch';
import ClienteInlineSearchField from '../../components/Tesoreria/ClienteInlineSearchField';
import AplicarSaldoReciboDialog from '../../components/Tesoreria/AplicarSaldoReciboDialog';
import api from '../../services/api';
import PDFViewer from '../../components/shared/PDFViewer';
import { getFileNameFromContentDisposition } from '../../utils/contentDisposition';
import { fetchFacturaVentaPdf } from '../../utils/facturaVentaPdf';
import {
  esCreditoAplicableDesdeCobranza,
  esDebitoCobranzaCliente,
  esDevolucionCobranzaCliente
} from '../../constants/tiposComprobante';
import ReplyIcon from '@mui/icons-material/Reply';

const formatterFecha = new Intl.DateTimeFormat('es-AR', {
  dateStyle: 'short'
});

/** Formatea yyyy-MM-dd en hora local (evita corrimiento -1 día de new Date('yyyy-MM-dd') = UTC). */
const formatFechaSoloDia = (fecha: string) =>
  formatterFecha.format(new Date(`${fecha}T12:00:00`));

/** Quita el prefijo redundante (FACTURA, RECIBO, ORDEN DE PAGO, NOTA DE CRÉDITO, etc.) del número de comprobante */
const numeroSinPrefijo = (numero: string | null | undefined): string => {
  if (!numero || !numero.trim()) return '-';
  const s = numero.trim();
  const match = s.match(/^(FACTURA|RECIBO|ORDEN\s+DE\s+PAGO|NOTA\s+DE\s+CR[EÉeé]D[IÍií]TO|NOTA\s+DE\s+D[EÉeé]B[IÍií]TO|DEVOLUCI[OÓoó]N|NCI|NDI|FC|ND|NC|REC|DEV)-/i);
  if (match) return s.slice(match[0].length).trim() || '-';
  return s || '-';
};

const esCreditoCobranzaAplicable = esCreditoAplicableDesdeCobranza;
const esDebitoClienteCobranza = esDebitoCobranzaCliente;

/** Mismo criterio que tesorería/recibos: nombre "ANULADO" o texto del comprobante */
const esComprobanteAnulado = (
  nombre: string | null | undefined,
  comprobanteTxt: string
): boolean =>
  (nombre ?? '').trim().toUpperCase() === 'ANULADO' || comprobanteTxt.includes('ANULADO');

type ResumenCuentaNavState = {
  from?: string;
  fromLabel?: string;
};

const INFORMES_VENTAS_PATH = '/ventas/informes';
const CUENTAS_COBRAR_PATH = '/ventas/informes/cuentas-cobrar';

const ResumenCuenta: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { nroCliente } = useParams<{ nroCliente: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const navState = (location.state as ResumenCuentaNavState | null) ?? null;
  const backTo =
    navState?.from === CUENTAS_COBRAR_PATH ? CUENTAS_COBRAR_PATH : INFORMES_VENTAS_PATH;
  const backLabel =
    navState?.from === CUENTAS_COBRAR_PATH
      ? navState.fromLabel?.trim() || 'Cuentas por Cobrar'
      : 'Informes';

  const [allComprobantes, setAllComprobantes] = useState<CuentaCorrienteComprobante[]>([]);
  const [clienteInfo, setClienteInfo] = useState<CuentaCorrienteSaldo | null>(null);
  const [openClienteSearch, setOpenClienteSearch] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentTab, setCurrentTab] = useState(0);
  const [fechaDesde, setFechaDesde] = useState<string>('');
  const [fechaHasta, setFechaHasta] = useState<string>('');
  const [searchTermMovimientos, setSearchTermMovimientos] = useState<string>('');
  const [pageMovimientos, setPageMovimientos] = useState(0);
  const [rowsPerPageMovimientos, setRowsPerPageMovimientos] = useState(10);
  const [movimientosHistoricos, setMovimientosHistoricos] = useState<MovimientoHistorico[]>([]);
  const [totalCountMovimientos, setTotalCountMovimientos] = useState(0);
  const [loadingMovimientos, setLoadingMovimientos] = useState(false);
  const [saldoInicialPaginaMovimientos, setSaldoInicialPaginaMovimientos] = useState(0);
  const [saldoInicialPeriodo, setSaldoInicialPeriodo] = useState<number | null>(null);
  const [exportingExcelMovimientos, setExportingExcelMovimientos] = useState(false);
  const [exportingPDFMovimientos, setExportingPDFMovimientos] = useState(false);
  const [showFiltrosMovimientos, setShowFiltrosMovimientos] = useState(false);
  const [searchTermComposicion, setSearchTermComposicion] = useState<string>('');
  const [showFiltrosComposicion, setShowFiltrosComposicion] = useState(false);
  const [openAplicarSaldoDialog, setOpenAplicarSaldoDialog] = useState(false);
  const [reciboIdAplicar, setReciboIdAplicar] = useState<number | null>(null);
  const [debitoIdVerAplicaciones, setDebitoIdVerAplicaciones] = useState<number | null>(null);
  const [debitoTipoVerAplicaciones, setDebitoTipoVerAplicaciones] = useState<string | undefined>(undefined);
  const [numeroComprobanteCreditoAplicar, setNumeroComprobanteCreditoAplicar] = useState<string | undefined>(
    undefined
  );
  const [numeroComprobanteDebitoAplicar, setNumeroComprobanteDebitoAplicar] = useState<string | undefined>(
    undefined
  );
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedReciboPdfId, setSelectedReciboPdfId] = useState<number | null>(null);
  const [selectedPdfDocType, setSelectedPdfDocType] = useState<'recibo' | 'factura' | 'devolucion'>('recibo');
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);

  const handleCloseAplicarDialog = () => {
    setOpenAplicarSaldoDialog(false);
    setReciboIdAplicar(null);
    setDebitoIdVerAplicaciones(null);
    setDebitoTipoVerAplicaciones(undefined);
    setNumeroComprobanteCreditoAplicar(undefined);
    setNumeroComprobanteDebitoAplicar(undefined);
  };

  const abrirVistaDebitoAplicaciones = (
    nroForm: number,
    nroTipo: string,
    comprobanteTxt = '',
    numeroComprobante = ''
  ) => {
    const tipoDebito = esDevolucionCobranzaCliente(nroTipo, comprobanteTxt) ? '60' : nroTipo;
    setDebitoIdVerAplicaciones(nroForm);
    setDebitoTipoVerAplicaciones(tipoDebito || undefined);
    setReciboIdAplicar(null);
    setNumeroComprobanteCreditoAplicar(undefined);
    const nro = numeroComprobante.trim();
    setNumeroComprobanteDebitoAplicar(nro && nro !== '-' ? nro : undefined);
    setOpenAplicarSaldoDialog(true);
  };

  const handleImprimirPDFRecibo = async (nroFormulario: number) => {
    try {
      const response = await api.get(`/api/tesoreria/recibos/${nroFormulario}/pdf`, {
        responseType: 'blob'
      });

      const cd = (response.headers as any)?.['content-disposition'] as string | undefined;
      const backendFileName = getFileNameFromContentDisposition(cd);
      setPdfFileName(backendFileName);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      setPdfUrl(url);
      setSelectedReciboPdfId(nroFormulario);
      setSelectedPdfDocType('recibo');
    } catch (error) {
      enqueueSnackbar('Error al generar el PDF del recibo', { variant: 'error' });
    }
  };

  const handleImprimirPDFDevolucion = async (nroFormulario: number) => {
    try {
      const response = await api.get(`/api/DevolucionesVenta/${nroFormulario}/pdf`, {
        responseType: 'blob',
      });

      const cd = (response.headers as any)?.['content-disposition'] as string | undefined;
      const backendFileName = getFileNameFromContentDisposition(cd);
      setPdfFileName(backendFileName);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      setPdfUrl(url);
      setSelectedReciboPdfId(nroFormulario);
      setSelectedPdfDocType('devolucion');
    } catch (error) {
      enqueueSnackbar('Error al generar el PDF de la devolución', { variant: 'error' });
    }
  };

  const handleImprimirPDFFactura = async (nroFormulario: number) => {
    try {
      const { url, fileName, avisoImpresion } = await fetchFacturaVentaPdf(nroFormulario);
      if (avisoImpresion) {
        enqueueSnackbar(avisoImpresion, { variant: 'warning', autoHideDuration: 12000 });
      }
      setPdfFileName(fileName);
      setPdfUrl(url);
      setSelectedReciboPdfId(nroFormulario);
      setSelectedPdfDocType('factura');
    } catch (error) {
      enqueueSnackbar('Error al generar el PDF de la factura', { variant: 'error' });
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    setSelectedReciboPdfId(null);
    setSelectedPdfDocType('recibo');
    setPdfFileName(null);
  };

  // Función auxiliar para cargar todos los comprobantes en lotes de 50 (límite del backend)
  const loadAllComprobantes = useCallback(async (clienteNum: number): Promise<CuentaCorrienteComprobante[]> => {
    const maxPageSize = 50;
    const result: CuentaCorrienteComprobante[] = [];
    let currentPage = 1;
    let hasMore = true;

    while (hasMore) {
      const response = await cuentaCorrienteService.getComprobantesCliente(clienteNum, {
        pageNumber: currentPage,
        pageSize: maxPageSize
      });

      result.push(...response.items);

      if (response.items.length < maxPageSize) {
        hasMore = false;
      } else {
        currentPage++;
      }
    }

    return result;
  }, []);

  const loadData = useCallback(async (clienteNum: number) => {
    try {
      setLoading(true);

      let clienteInfoData: CuentaCorrienteSaldo | null = null;
      try {
        clienteInfoData = await cuentaCorrienteService.getSaldoCliente(clienteNum);
      } catch (error: unknown) {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 404) {
          try {
            const cli = await clienteService.getCliente(clienteNum);
            clienteInfoData = {
              nroCliente: cli.nroCliente,
              nombre: cli.nombre || '',
              saldoTotal: 0,
              saldoVencido: 0,
              saldoVigente: 0,
              ultimaActualizacion: ''
            };
          } catch {
            enqueueSnackbar('Cliente no encontrado.', { variant: 'error' });
            setClienteInfo(null);
            setAllComprobantes([]);
            return;
          }
        } else {
          console.error('Error al cargar los saldos:', error);
          enqueueSnackbar('Error al cargar los saldos del cliente.', { variant: 'error' });
          setClienteInfo(null);
          setAllComprobantes([]);
          return;
        }
      }

      if (!clienteInfoData) {
        setClienteInfo(null);
        setAllComprobantes([]);
        return;
      }

      setClienteInfo(clienteInfoData);

      const comprobantes = await loadAllComprobantes(clienteNum);
      const comprobantesOrdenados = [...comprobantes].sort((a, b) => {
        const fechaA = a.fecha ? new Date(a.fecha).getTime() : 0;
        const fechaB = b.fecha ? new Date(b.fecha).getTime() : 0;
        if (fechaA !== fechaB) return fechaA - fechaB;
        const numeroA = a.numero || '';
        const numeroB = b.numero || '';
        return numeroA.localeCompare(numeroB);
      });

      setAllComprobantes(comprobantesOrdenados);
      setPage(0);
    } catch (error) {
      console.error('Error al obtener los datos:', error);
      enqueueSnackbar('No se pudieron cargar los datos.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, loadAllComprobantes]);

  useEffect(() => {
    if (nroCliente) {
      loadData(Number(nroCliente));
    }
  }, [nroCliente, loadData]);

  const loadMovimientosHistoricos = useCallback(async () => {
    if (!nroCliente || !clienteInfo) return;
    try {
      setLoadingMovimientos(true);
      const limitedPageSize = Math.min(rowsPerPageMovimientos, 50);
      const response = await cuentaCorrienteService.getMovimientosHistoricosCliente(
        Number(nroCliente),
        {
          fechaDesde: fechaDesde || undefined,
          fechaHasta: fechaHasta || undefined,
          searchTerm: searchTermMovimientos.trim() || undefined,
          pageNumber: pageMovimientos + 1,
          pageSize: limitedPageSize
        }
      );
      setMovimientosHistoricos(response.items);
      setTotalCountMovimientos(response.totalCount);
      setSaldoInicialPeriodo(response.saldoInicial ?? null);

      // Calcular el saldo inicial de la página: saldo inicial del período (si hay filtro) + suma de páginas anteriores
      let saldoInicial = response.saldoInicial ?? 0;
      if (pageMovimientos > 0) {
        try {
          const totalAnteriores = pageMovimientos * limitedPageSize;
          const maxPageSize = 50;
          for (let offset = 0; offset < totalAnteriores; offset += maxPageSize) {
            const currentPageSize = Math.min(maxPageSize, totalAnteriores - offset);
            const currentPageNumber = Math.floor(offset / maxPageSize) + 1;
            const lote = await cuentaCorrienteService.getMovimientosHistoricosCliente(
              Number(nroCliente),
              {
                fechaDesde: fechaDesde || undefined,
                fechaHasta: fechaHasta || undefined,
                searchTerm: searchTermMovimientos.trim() || undefined,
                pageNumber: currentPageNumber,
                pageSize: currentPageSize
              }
            );
            for (const item of lote.items) {
              saldoInicial += (item.impCompDebe ?? 0) - (item.impCompHaber ?? 0);
            }
          }
        } catch (err) {
          console.warn('No se pudo calcular el saldo inicial de la página:', err);
        }
      }
      setSaldoInicialPaginaMovimientos(saldoInicial);
    } catch (error) {
      console.error('Error al cargar movimientos históricos:', error);
      enqueueSnackbar('No se pudieron cargar los movimientos históricos.', { variant: 'error' });
      setMovimientosHistoricos([]);
      setTotalCountMovimientos(0);
      setSaldoInicialPaginaMovimientos(0);
      setSaldoInicialPeriodo(null);
    } finally {
      setLoadingMovimientos(false);
    }
  }, [
    nroCliente,
    clienteInfo,
    fechaDesde,
    fechaHasta,
    searchTermMovimientos,
    pageMovimientos,
    rowsPerPageMovimientos,
    enqueueSnackbar
  ]);

  const loadMovimientosHistoricosRef = useRef(loadMovimientosHistoricos);
  loadMovimientosHistoricosRef.current = loadMovimientosHistoricos;

  // No incluir filtros (fecha/búsqueda) en deps: se aplican con "Buscar" / Enter.
  // Si no, cada tecla o cambio parcial de fecha dispara el API y muestra errores.
  useEffect(() => {
    if (nroCliente && clienteInfo && currentTab === 1) {
      loadMovimientosHistoricosRef.current();
    }
  }, [nroCliente, clienteInfo, currentTab, pageMovimientos, rowsPerPageMovimientos]);

  const handleBuscarMovimientos = () => {
    if (pageMovimientos === 0) {
      void loadMovimientosHistoricos();
    } else {
      setPageMovimientos(0);
    }
  };

  const loadAllMovimientosHistoricos = useCallback(async (): Promise<{
    items: MovimientoHistorico[];
    saldoInicial: number | null;
  }> => {
    const maxPageSize = 50;
    const allItems: MovimientoHistorico[] = [];
    let saldoInicial: number | null = null;
    let currentPage = 1;
    let hasMore = true;

    while (hasMore) {
      const response = await cuentaCorrienteService.getMovimientosHistoricosCliente(
        Number(nroCliente!),
        {
          fechaDesde: fechaDesde || undefined,
          fechaHasta: fechaHasta || undefined,
          searchTerm: searchTermMovimientos.trim() || undefined,
          pageNumber: currentPage,
          pageSize: maxPageSize
        }
      );
      allItems.push(...response.items);
      if (currentPage === 1 && response.saldoInicial !== undefined) {
        saldoInicial = response.saldoInicial;
      }
      if (response.items.length < maxPageSize) hasMore = false;
      else currentPage++;
    }
    return { items: allItems, saldoInicial };
  }, [nroCliente, fechaDesde, fechaHasta, searchTermMovimientos]);

  const handleExportExcelMovimientos = async () => {
    if (totalCountMovimientos === 0 || !nroCliente || !clienteInfo) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
      return;
    }
    try {
      setExportingExcelMovimientos(true);
      const { items, saldoInicial } = await loadAllMovimientosHistoricos();
      const data: Record<string, string | number>[] = [];
      if (fechaDesde && saldoInicial !== null) {
        data.push({
          'Fecha Emisión': `Saldo Inicial al ${formatFechaSoloDia(fechaDesde)}`,
          'Comprobante': '',
          'Número': '',
          'Descripción': '',
          'Importe Total': '',
          'Debe': '',
          'Haber': '',
          'Saldo Acumulado': saldoInicial,
          'Fecha Venc.': '',
          Estado: ''
        });
      }
      let saldoAcumulado = saldoInicial ?? 0;
      items.forEach((item) => {
        saldoAcumulado += (item.impCompDebe ?? 0) - (item.impCompHaber ?? 0);
        const descripcionTipTxt = (item.descripcionTip ?? '').toString().toUpperCase();
        const nroTipo = (item.nroTipoFormulario ?? '').toString().trim();
        const esRecibo = nroTipo === '4' || descripcionTipTxt.includes('RECIBO');
        const esAnulado = esComprobanteAnulado(item.nombre, descripcionTipTxt);
        data.push({
          'Fecha Emisión': item.fechaEmision ? formatterFecha.format(new Date(item.fechaEmision)) : '',
          'Comprobante': item.descripcionTip || '',
          'Número': numeroSinPrefijo(item.numero) === '-' ? '' : numeroSinPrefijo(item.numero),
          'Descripción': item.descripcionFor || '',
          'Importe Total': item.importeTotal ?? 0,
          'Debe': item.impCompDebe ?? 0,
          'Haber': item.impCompHaber ?? 0,
          'Saldo Acumulado': saldoAcumulado,
          'Fecha Venc.': item.fechaVencim ? formatterFecha.format(new Date(item.fechaVencim)) : '',
          Estado: esAnulado && esRecibo ? 'Anulado' : ''
        });
      });
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'MovimientosHistoricos');
      const today = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `movimientos_historicos_cliente_${nroCliente}_${today}.xlsx`);
      enqueueSnackbar('Exportación a Excel completada.', { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      enqueueSnackbar('No se pudo exportar a Excel.', { variant: 'error' });
    } finally {
      setExportingExcelMovimientos(false);
    }
  };

  const handleExportPDFMovimientos = async () => {
    if (totalCountMovimientos === 0 || !nroCliente || !clienteInfo) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
      return;
    }
    try {
      setExportingPDFMovimientos(true);
      const { items, saldoInicial } = await loadAllMovimientosHistoricos();
      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;
      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const marginX = 40;
      const marginY = 50;
      let currentY = marginY;

      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Movimientos Históricos - Cuenta Corriente', marginX, currentY);
      currentY += 24;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Cliente: ${nroCliente} - ${clienteInfo.nombre}`, marginX, currentY);
      currentY += 16;
      if (fechaDesde || fechaHasta) {
        doc.text(
          `Período: ${fechaDesde ? formatFechaSoloDia(fechaDesde) : '...'} - ${fechaHasta ? formatFechaSoloDia(fechaHasta) : '...'}`,
          marginX,
          currentY
        );
        currentY += 16;
      }
      currentY += 8;

      const tableData: (string | number)[][] = [];
      if (fechaDesde && saldoInicial !== null) {
        tableData.push([
          `Saldo Inicial al ${formatFechaSoloDia(fechaDesde)}`,
          '',
          '',
          '',
          '',
          '',
          '',
          formatCurrency(saldoInicial),
          '',
          ''
        ]);
      }
      let saldoAcumulado = saldoInicial ?? 0;
      items.forEach((item) => {
        saldoAcumulado += (item.impCompDebe ?? 0) - (item.impCompHaber ?? 0);
        const descripcionTipTxt = (item.descripcionTip ?? '').toString().toUpperCase();
        const nroTipo = (item.nroTipoFormulario ?? '').toString().trim();
        const esRecibo = nroTipo === '4' || descripcionTipTxt.includes('RECIBO');
        const esAnulado = esComprobanteAnulado(item.nombre, descripcionTipTxt);
        tableData.push([
          item.fechaEmision ? formatterFecha.format(new Date(item.fechaEmision)) : '',
          item.descripcionTip || '',
          numeroSinPrefijo(item.numero) === '-' ? '' : numeroSinPrefijo(item.numero),
          item.descripcionFor || '',
          formatCurrency(item.importeTotal ?? 0),
          formatCurrency(item.impCompDebe ?? 0),
          formatCurrency(item.impCompHaber ?? 0),
          formatCurrency(saldoAcumulado),
          item.fechaVencim ? formatterFecha.format(new Date(item.fechaVencim)) : '',
          esAnulado && esRecibo ? 'Anulado' : ''
        ]);
      });

      autoTable(doc, {
        startY: currentY,
        head: [['Fecha Emisión', 'Comprobante', 'Número', 'Descripción', 'Importe', 'Debe', 'Haber', 'Saldo Acumulado', 'Fecha Venc.', 'Estado']],
        body: tableData,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 7, cellPadding: 3, overflow: 'linebreak' },
        headStyles: { fillColor: [25, 118, 210], textColor: 255, fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 50 },
          1: { cellWidth: 45 },
          2: { cellWidth: 55 },
          3: { cellWidth: 65 },
          4: { halign: 'right', cellWidth: 50 },
          5: { halign: 'right', cellWidth: 40 },
          6: { halign: 'right', cellWidth: 40 },
          7: { halign: 'right', cellWidth: 55 },
          8: { cellWidth: 45 },
          9: { cellWidth: 40 }
        },
        tableWidth: 'auto'
      });
      currentY = (doc as { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY ?? currentY;
      currentY += 16;
      doc.setFont('helvetica', 'bold');
      doc.text(`Saldo Cuenta Corriente: ${formatCurrency(clienteInfo.saldoTotal)}`, marginX, currentY);

      const pdfBlob = doc.output('blob');
      const today = new Date().toISOString().slice(0, 10);
      const fileName = `movimientos_historicos_cliente_${nroCliente}_${today}.pdf`;
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.target = '_self';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      enqueueSnackbar('PDF descargado correctamente.', { variant: 'success' });
    } catch (error) {
      console.error('Error al generar el PDF:', error);
      enqueueSnackbar('No se pudo generar el PDF.', { variant: 'error' });
    } finally {
      setExportingPDFMovimientos(false);
    }
  };

  const handleClienteSelect = (cliente: { nroCliente: number }) => {
    navigate(`/ventas/informes/resumen-cuenta/${cliente.nroCliente}`, {
      state: navState ?? undefined,
    });
  };

  const fechaSoloDia = (fecha: string | null | undefined): string => {
    if (!fecha) return '';
    return fecha.length >= 10 ? fecha.slice(0, 10) : fecha;
  };

  const comprobantesFiltrados = allComprobantes.filter((item) => {
    const fechaItem = fechaSoloDia(item.fecha);
    if (!fechaItem) {
      if (fechaDesde || fechaHasta) return false;
    } else {
      if (fechaDesde && fechaItem < fechaDesde) return false;
      if (fechaHasta && fechaItem > fechaHasta) return false;
    }

    const term = searchTermComposicion.trim().toLowerCase();
    if (term) {
      const comprobante = (item.comprobante ?? '').toLowerCase();
      const numero = (item.numero ?? '').toLowerCase();
      if (!comprobante.includes(term) && !numero.includes(term)) return false;
    }
    return true;
  });

  const saldoInicialComposicion = (() => {
    if (!fechaDesde) return null;
    let saldo = 0;
    for (const item of allComprobantes) {
      const fechaItem = fechaSoloDia(item.fecha);
      if (fechaItem && fechaItem < fechaDesde) {
        saldo += item.saldoDeb - item.saldoHab;
      }
    }
    return saldo;
  })();

  const totalCount = comprobantesFiltrados.length;
  const maxPageComposicion = Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1);
  const pageComposicion = Math.min(page, maxPageComposicion);
  const startIndex = pageComposicion * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const comprobantes = comprobantesFiltrados.slice(startIndex, endIndex);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePageMovimientos = (_: unknown, newPage: number) => {
    setPageMovimientos(newPage);
  };

  const handleChangeRowsPerPageMovimientos = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    const limited = Math.min(newRowsPerPage, 50);
    setRowsPerPageMovimientos(limited);
    setPageMovimientos(0);
  };

  const handleExportExcel = async () => {
    if (totalCount === 0) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
      return;
    }

    try {
      setExportingExcel(true);

      const data: Record<string, string | number | null | undefined>[] = [];
      let saldoAcumulado = saldoInicialComposicion ?? 0;

      if (fechaDesde && saldoInicialComposicion !== null) {
        data.push({
          Fecha: `Saldo Inicial al ${formatFechaSoloDia(fechaDesde)}`,
          Comprobante: '',
          Número: '',
          Importe: '',
          Debe: '',
          Haber: '',
          'Saldo Acumulado': saldoInicialComposicion,
          'Fecha Vencimiento': '',
          'Nro. Formulario': ''
        });
      }

      for (const item of comprobantesFiltrados) {
        saldoAcumulado += item.saldoDeb - item.saldoHab;
        data.push({
          Fecha: item.fecha ? formatterFecha.format(new Date(item.fecha)) : '',
          Comprobante: item.comprobante,
          Número: numeroSinPrefijo(item.numero) === '-' ? '' : numeroSinPrefijo(item.numero),
          Importe: item.importe,
          Debe: item.saldoDeb,
          Haber: item.saldoHab,
          'Saldo Acumulado': saldoAcumulado,
          'Fecha Vencimiento': item.fechaVenc ? formatterFecha.format(new Date(item.fechaVenc)) : '',
          'Nro. Formulario': item.nroFormulario
        });
      }

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'ResumenCuenta');

      const today = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `resumen_cuenta_cliente_${nroCliente}_${today}.xlsx`);
      enqueueSnackbar('Exportación a Excel completada.', { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      enqueueSnackbar('No se pudo exportar a Excel.', { variant: 'error' });
    } finally {
      setExportingExcel(false);
    }
  };

  const handleExportPDF = async () => {
    if (totalCount === 0 || !clienteInfo) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
      return;
    }

    try {
      setExportingPDF(true);

      if (!comprobantesFiltrados.length) {
        enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
        return;
      }

      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;

      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const marginX = 40;
      const marginY = 50;
      let currentY = marginY;

      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Resumen de Cuenta - Cuenta Corriente', marginX, currentY);
      currentY += 24;

      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Cliente: ${nroCliente} - ${clienteInfo.nombre}`, marginX, currentY);
      currentY += 20;

      if (fechaDesde || fechaHasta) {
        doc.text(
          `Período: ${fechaDesde ? formatFechaSoloDia(fechaDesde) : '...'} - ${fechaHasta ? formatFechaSoloDia(fechaHasta) : '...'}`,
          marginX,
          currentY
        );
        currentY += 20;
      }

      let saldoAcumulado = saldoInicialComposicion ?? 0;
      const tableData: string[][] = [];

      if (fechaDesde && saldoInicialComposicion !== null) {
        tableData.push([
          `Saldo Inicial al ${formatFechaSoloDia(fechaDesde)}`,
          '',
          '',
          '-',
          '-',
          '-',
          formatCurrency(saldoInicialComposicion),
          ''
        ]);
      }

      for (const item of comprobantesFiltrados) {
        saldoAcumulado += item.saldoDeb - item.saldoHab;
        tableData.push([
          item.fecha ? formatterFecha.format(new Date(item.fecha)) : '',
          item.comprobante || '',
          numeroSinPrefijo(item.numero) === '-' ? '' : numeroSinPrefijo(item.numero),
          formatCurrency(item.importe),
          formatCurrency(item.saldoDeb),
          formatCurrency(item.saldoHab),
          formatCurrency(saldoAcumulado),
          item.fechaVenc ? formatterFecha.format(new Date(item.fechaVenc)) : ''
        ]);
      }

      autoTable(doc, {
        startY: currentY,
        head: [['Fecha', 'Comprobante', 'Número', 'Importe', 'Debe', 'Haber', 'Saldo Acumulado', 'Fecha Venc.']],
        body: tableData,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 7, cellPadding: 3, overflow: 'linebreak' },
        headStyles: { fillColor: [25, 118, 210], textColor: 255, fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 90 },
          2: { cellWidth: 70 },
          3: { halign: 'right', cellWidth: 70 },
          4: { halign: 'right', cellWidth: 50 },
          5: { halign: 'right', cellWidth: 50 },
          6: { halign: 'right', cellWidth: 75 },
          7: { cellWidth: 70 }
        },
        tableWidth: 'auto'
      });
      currentY = (doc as { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY ?? currentY;
      currentY += 16;
      doc.setFont('helvetica', 'bold');
      doc.text(`Saldo Cuenta Corriente: ${formatCurrency(clienteInfo.saldoTotal)}`, marginX, currentY);

      const pdfBlob = doc.output('blob');
      const today = new Date().toISOString().slice(0, 10);
      const fileName = `resumen_cuenta_cliente_${nroCliente}_${today}.pdf`;

      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.target = '_self';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      enqueueSnackbar('PDF descargado correctamente.', { variant: 'success' });
    } catch (error) {
      console.error('Error al generar el PDF:', error);
      enqueueSnackbar('No se pudo generar el PDF.', { variant: 'error' });
    } finally {
      setExportingPDF(false);
    }
  };

  const calcularSaldoAcumulado = (indexInPage: number): number => {
    const globalIndex = startIndex + indexInPage;
    let saldo = saldoInicialComposicion ?? 0;
    for (let i = 0; i <= globalIndex; i++) {
      if (comprobantesFiltrados[i]) {
        saldo += comprobantesFiltrados[i].saldoDeb - comprobantesFiltrados[i].saldoHab;
      }
    }
    return saldo;
  };

  const mostrarSaldoInicialComposicion =
    pageComposicion === 0 &&
    Boolean(fechaDesde) &&
    saldoInicialComposicion !== null;
  const mostrarSaldoInicialHistorico =
    Number(pageMovimientos) === 0 &&
    Boolean(fechaDesde) &&
    saldoInicialPeriodo !== null;

  const calcularSaldoAcumuladoMovimientos = (indexInPage: number): number => {
    let saldo = saldoInicialPaginaMovimientos;
    for (let i = 0; i <= indexInPage; i++) {
      if (movimientosHistoricos[i]) {
        saldo += (movimientosHistoricos[i].impCompDebe ?? 0) - (movimientosHistoricos[i].impCompHaber ?? 0);
      }
    }
    return saldo;
  };

  /** Botonera de acciones (aplicar saldo / imprimir PDF) compartida entre la tabla de comprobantes y la de movimientos históricos. */
  const renderAccionesComprobante = (
    nroForm: number | null | undefined,
    nroTipo: string,
    textoTipo: string,
    numero: string | null | undefined,
    esAnulado: boolean
  ): React.ReactNode => {
    if (!nroForm) return '-';
    const esCredito = esCreditoCobranzaAplicable(nroTipo, textoTipo);
    const esDebitoImp = esDebitoClienteCobranza(nroTipo, textoTipo);
    const esReciboPdf = nroTipo === '4' || textoTipo.includes('RECIBO');
    const esDevolucion = esDevolucionCobranzaCliente(nroTipo, textoTipo);
    const esFacturaPdf = nroTipo === '1' || textoTipo.includes('FACTURA') || textoTipo.includes('FC');
    const sxWallet = {
      bgcolor: '#2e7d32',
      color: '#fff',
      borderRadius: 1.5,
      '&:hover': { bgcolor: '#1b5e20', transform: 'scale(1.1)' },
      transition: 'all 0.2s'
    };
    const sxPrint = {
      bgcolor: '#2196f3',
      color: '#fff',
      borderRadius: 1.5,
      '&:hover': { bgcolor: '#1976d2', transform: 'scale(1.1)' },
      transition: 'all 0.2s'
    };
    const nodes: React.ReactNode[] = [];
    if (esCredito && !esAnulado) {
      nodes.push(
        <Tooltip key="w-cred" title="Aplicar saldo / Ver aplicaciones">
          <IconButton
            size="small"
            onClick={() => {
              setReciboIdAplicar(nroForm);
              setDebitoIdVerAplicaciones(null);
              const nro = numeroSinPrefijo(numero);
              setNumeroComprobanteCreditoAplicar(nro !== '-' ? nro : undefined);
              setNumeroComprobanteDebitoAplicar(undefined);
              setOpenAplicarSaldoDialog(true);
            }}
            sx={sxWallet}
          >
            <AccountBalanceWalletIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    }
    if (esDebitoImp) {
      nodes.push(
        <Tooltip key="w-deb" title="Aplicar saldo / Ver aplicaciones">
          <IconButton
            size="small"
            onClick={() => abrirVistaDebitoAplicaciones(nroForm, nroTipo, textoTipo, numeroSinPrefijo(numero))}
            sx={sxWallet}
          >
            <AccountBalanceWalletIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    }
    if (esReciboPdf) {
      nodes.push(
        <Tooltip key="pdf-rec" title="Imprimir PDF">
          <IconButton size="small" onClick={() => handleImprimirPDFRecibo(nroForm)} sx={sxPrint}>
            <PrintIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    }
    if (esDevolucion && !esReciboPdf) {
      nodes.push(
        <Tooltip key="pdf-dev" title="Imprimir PDF">
          <IconButton size="small" onClick={() => handleImprimirPDFDevolucion(nroForm)} sx={sxPrint}>
            <PrintIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    }
    if (esFacturaPdf) {
      nodes.push(
        <Tooltip key="pdf-fac" title="Imprimir PDF">
          <IconButton size="small" onClick={() => handleImprimirPDFFactura(nroForm)} sx={sxPrint}>
            <PrintIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    }
    if (nodes.length === 0) return '-';
    return (
      <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap" useFlexGap>
        {nodes}
      </Stack>
    );
  };

  /** Botonera de acciones para la tabla/cards de Composición de Saldo. */
  const renderAccionesComposicion = (item: CuentaCorrienteComprobante): React.ReactNode => {
    const comprobanteTxt = (item.comprobante ?? '').toString().toUpperCase();
    return renderAccionesComprobante(
      item.nroFormulario,
      (item.nroTipoFormulario ?? '').toString().trim(),
      comprobanteTxt,
      item.numero,
      esComprobanteAnulado(item.nombre, comprobanteTxt)
    );
  };

  /** Botonera de acciones para la tabla/cards de Movimientos Históricos. */
  const renderAccionesMovimiento = (item: MovimientoHistorico): React.ReactNode => {
    const descripcionTipTxt = (item.descripcionTip ?? '').toString().toUpperCase();
    return renderAccionesComprobante(
      item.nroFormulario,
      (item.nroTipoFormulario ?? '').toString().trim(),
      descripcionTipTxt,
      item.numero,
      esComprobanteAnulado(item.nombre, descripcionTipTxt)
    );
  };

  const renderCardCampo = (label: string, valor: React.ReactNode, sx?: object) => (
    <Box sx={sx}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2">{valor}</Typography>
    </Box>
  );

  const renderCardsComposicionMobile = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={120} p={3}>
          <CircularProgress />
        </Box>
      );
    }
    if (comprobantes.length === 0) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={120} p={3}>
          <Typography>No hay comprobantes para este cliente.</Typography>
        </Box>
      );
    }
    return (
      <Stack spacing={2}>
        {comprobantes.map((item, index) => {
          const saldoAcumulado = calcularSaldoAcumulado(index);
          const vencido = Boolean(item.fechaVenc && new Date(item.fechaVenc) < new Date());
          return (
            <Paper key={`${item.numero}-${index}`} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack spacing={1.5}>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    {renderCardCampo('Fecha', item.fecha ? formatterFecha.format(new Date(item.fecha)) : '-')}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo('Comprobante', item.comprobante || '-')}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo('Número', numeroSinPrefijo(item.numero))}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo('Importe Total', formatCurrency(item.importe))}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo('Saldo Debe', formatCurrency(item.saldoDeb))}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo('Saldo Haber', formatCurrency(item.saldoHab))}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo(
                      'Saldo Acumulado',
                      <Box component="span" sx={{ fontWeight: 600, color: '#1976d2' }}>
                        {formatCurrency(saldoAcumulado)}
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo(
                      'Fecha Venc.',
                      <Box component="span" sx={vencido ? { color: '#d32f2f', fontWeight: 600 } : undefined}>
                        {item.fechaVenc ? formatterFecha.format(new Date(item.fechaVenc)) : '-'}
                      </Box>
                    )}
                  </Grid>
                </Grid>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    Acciones
                  </Typography>
                  {renderAccionesComposicion(item)}
                </Box>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    );
  };

  const renderCardsMovimientosMobile = () => {
    if (loadingMovimientos) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={120} p={3}>
          <CircularProgress />
        </Box>
      );
    }
    if (movimientosHistoricos.length === 0) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={120} p={3}>
          <Typography>No hay movimientos para este cliente con los filtros aplicados.</Typography>
        </Box>
      );
    }
    return (
      <Stack spacing={2}>
        {fechaDesde && saldoInicialPeriodo !== null && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#f0f7ff' }}>
            <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 0.5 }}>
              Saldo Inicial al {formatterFecha.format(new Date(fechaDesde))}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {formatCurrency(saldoInicialPeriodo)}
            </Typography>
          </Paper>
        )}
        {movimientosHistoricos.map((item, index) => {
          const saldoAcumulado = calcularSaldoAcumuladoMovimientos(index);
          const nroTipo = (item.nroTipoFormulario ?? '').toString().trim();
          const descripcionTipTxt = (item.descripcionTip ?? '').toString().toUpperCase();
          const esAnulado = esComprobanteAnulado(item.nombre, descripcionTipTxt);
          const esRecibo = nroTipo === '4' || descripcionTipTxt.includes('RECIBO');
          const vencido = Boolean(item.fechaVencim && new Date(item.fechaVencim) < new Date());
          return (
            <Paper key={`${item.numero}-${item.fechaEmision}-${index}`} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack spacing={1.5}>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    {renderCardCampo('Fecha Emisión', item.fechaEmision ? formatterFecha.format(new Date(item.fechaEmision)) : '-')}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo('Comprobante', item.descripcionTip || '-')}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo('Número', numeroSinPrefijo(item.numero))}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo('Descripción', item.descripcionFor || '-')}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo('Importe Total', formatCurrency(item.importeTotal ?? 0))}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo('Debe', formatCurrency(item.impCompDebe ?? 0))}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo('Haber', formatCurrency(item.impCompHaber ?? 0))}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo(
                      'Saldo Acumulado',
                      <Box component="span" sx={{ fontWeight: 600, color: '#1976d2' }}>
                        {formatCurrency(saldoAcumulado)}
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo(
                      'Fecha Venc.',
                      <Box component="span" sx={vencido ? { color: '#d32f2f', fontWeight: 600 } : undefined}>
                        {item.fechaVencim ? formatterFecha.format(new Date(item.fechaVencim)) : '-'}
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    {renderCardCampo(
                      'Estado',
                      esAnulado && esRecibo ? (
                        <Chip
                          label="Anulado"
                          size="small"
                          sx={{ bgcolor: '#9e9e9e', color: '#fff', fontWeight: 500, borderRadius: 1.5, minWidth: 72 }}
                        />
                      ) : (
                        '-'
                      )
                    )}
                  </Grid>
                </Grid>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    Acciones
                  </Typography>
                  {renderAccionesMovimiento(item)}
                </Box>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      {/* Tarjeta blanca contenedora principal */}
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header con título */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 3 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(backTo)}
              sx={{ textTransform: 'none' }}
            >
              {backLabel}
            </Button>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
              Resumen de Cuenta Corriente de Clientes
            </Typography>
          </Stack>

          {/* Formulario de búsqueda */}
          <Card 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              bgcolor: '#fafafa'
            }}
          >
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <ClienteInlineSearchField
                    label="Cliente"
                    placeholder="Número, nombre o CUIT"
                    excluirInactivos={false}
                    displayValue={
                      clienteInfo
                        ? `${clienteInfo.nroCliente} - ${clienteInfo.nombre}`
                        : nroCliente ?? ''
                    }
                    onSelect={handleClienteSelect}
                    onOpenModal={() => setOpenClienteSearch(true)}
                  />
                </Grid>
                {clienteInfo && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Nombre"
                        value={clienteInfo.nombre}
                        disabled
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Saldo Total"
                        value={formatCurrency(clienteInfo.saldoTotal)}
                        disabled
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '& .MuiInputBase-input': {
                              fontWeight: 600,
                              color: '#1976d2',
                            }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Saldo Vencido"
                        value={formatCurrency(clienteInfo.saldoVencido ?? 0)}
                        disabled
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '& .MuiInputBase-input': {
                              fontWeight: 600,
                              color: clienteInfo.saldoVencido && clienteInfo.saldoVencido > 0 ? '#d32f2f' : 'inherit',
                            }
                          }
                        }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Tabs */}
          {clienteInfo && (
            <Tabs
              value={currentTab}
              onChange={(_, v: number) => setCurrentTab(v)}
              variant={isMobile ? 'scrollable' : 'standard'}
              scrollButtons={isMobile ? 'auto' : false}
              allowScrollButtonsMobile
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Composición de Saldo" />
              <Tab label="Movimientos Históricos" />
            </Tabs>
          )}

          {/* Mensaje cuando no hay cliente seleccionado */}
          {!clienteInfo && !loading && (
            <Card
              sx={{
                p: 3,
                textAlign: 'center',
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                bgcolor: '#fafafa'
              }}
            >
              <Typography variant="body1" color="text.secondary">
                Seleccione un cliente para ver la composición de su saldo
              </Typography>
            </Card>
          )}

          {/* Tab 0: Composición de Saldo */}
          {clienteInfo && currentTab === 0 && (
            <>
              <Box mb={2}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={2}
                >
                  <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<FilterListIcon />}
                    endIcon={showFiltrosComposicion ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={() => setShowFiltrosComposicion(!showFiltrosComposicion)}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                  >
                    Filtrar
                  </Button>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<ReplyIcon />}
                      onClick={() => {
                        if (clienteInfo?.nroCliente) {
                          navigate(`/tesoreria/devoluciones/nuevo?nroCliente=${clienteInfo.nroCliente}`);
                        }
                      }}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                      }}
                    >
                      Nueva devolución
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<AccountBalanceWalletIcon />}
                      onClick={() => {
                        setReciboIdAplicar(null);
                        setDebitoIdVerAplicaciones(null);
                        setNumeroComprobanteCreditoAplicar(undefined);
                        setNumeroComprobanteDebitoAplicar(undefined);
                        setOpenAplicarSaldoDialog(true);
                      }}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                      }}
                    >
                      Aplicar saldos pendientes
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<FileDownloadIcon />}
                      onClick={handleExportExcel}
                      disabled={loading || exportingExcel || totalCount === 0}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                      }}
                    >
                      {exportingExcel ? 'Exportando...' : 'Exportar Excel'}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={handleExportPDF}
                      disabled={loading || exportingPDF || totalCount === 0}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                        }
                      }}
                    >
                      {exportingPDF ? 'Generando...' : 'Exportar PDF'}
                    </Button>
                  </Stack>
                </Box>
                <Collapse in={showFiltrosComposicion} timeout="auto" unmountOnExit>
                  <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" sx={{ mt: 2 }}>
                    <TextField
                      size="small"
                      label="Fecha Desde"
                      type="date"
                      value={fechaDesde}
                      onChange={(e) => { setFechaDesde(e.target.value); setPage(0); setPageMovimientos(0); }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 150, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <TextField
                      size="small"
                      label="Fecha Hasta"
                      type="date"
                      value={fechaHasta}
                      onChange={(e) => { setFechaHasta(e.target.value); setPage(0); setPageMovimientos(0); }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 150, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <TextField
                      size="small"
                      label="Buscar"
                      placeholder="Comprobante, número..."
                      value={searchTermComposicion}
                      onChange={(e) => { setSearchTermComposicion(e.target.value); setPage(0); }}
                      onKeyPress={(e) => { if (e.key === 'Enter') setPage(0); }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon color="action" fontSize="small" />
                          </InputAdornment>
                        )
                      }}
                      sx={{ width: 220, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      onClick={() => setPage(0)}
                      sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                      Buscar
                    </Button>
                  </Box>
                </Collapse>
              </Box>

          {/* Tabla de comprobantes (desktop) / Cards (mobile) */}
          {isMobile ? (
            <Box>
              {renderCardsComposicionMobile()}
              {comprobantes.length > 0 && (
                <TablePagination
                  rowsPerPageOptions={[10, 20, 50]}
                  component="div"
                  count={totalCount}
                  rowsPerPage={rowsPerPage}
                  page={pageComposicion}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Filas por página"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                  sx={{
                    mt: 2,
                    '& .MuiTablePagination-toolbar': {
                      flexWrap: 'wrap',
                      gap: 1,
                      px: 1
                    }
                  }}
                />
              )}
            </Box>
          ) : (
            <TableContainer 
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
              }}
            >
              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={200} p={3}>
                  <CircularProgress />
                </Box>
              ) : totalCount === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={200} p={3}>
                  <Typography>
                    {fechaDesde || fechaHasta || searchTermComposicion.trim()
                      ? 'No hay comprobantes para este cliente con los filtros aplicados.'
                      : 'No hay comprobantes para este cliente.'}
                  </Typography>
                </Box>
              ) : (
                <>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Comprobante</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Número</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Importe Total</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Saldo Debe</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Saldo Haber</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Saldo Acumulado</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Fecha Venc.</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mostrarSaldoInicialComposicion && (
                        <TableRow
                          key="saldo-inicial-composicion"
                          sx={{ bgcolor: '#f0f7ff', '&:hover': { bgcolor: '#e8f0fe' } }}
                        >
                          <TableCell colSpan={3} sx={{ fontStyle: 'italic' }}>
                            Saldo Inicial al {formatFechaSoloDia(fechaDesde)}
                          </TableCell>
                          <TableCell align="right">-</TableCell>
                          <TableCell align="right">-</TableCell>
                          <TableCell align="right">-</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
                            {formatCurrency(saldoInicialComposicion as number)}
                          </TableCell>
                          <TableCell>-</TableCell>
                          <TableCell align="center">-</TableCell>
                        </TableRow>
                      )}
                      {comprobantes.map((item, index) => {
                        const saldoAcumulado = calcularSaldoAcumulado(index);
                        return (
                          <TableRow 
                            key={`${item.numero}-${index}`}
                            sx={{
                              '&:hover': {
                                bgcolor: '#f9f9f9',
                              },
                            }}
                          >
                            <TableCell>
                              {item.fecha ? formatterFecha.format(new Date(item.fecha)) : '-'}
                            </TableCell>
                            <TableCell>{item.comprobante || '-'}</TableCell>
                            <TableCell>{numeroSinPrefijo(item.numero)}</TableCell>
                            <TableCell align="right">{formatCurrency(item.importe)}</TableCell>
                            <TableCell align="right">{formatCurrency(item.saldoDeb)}</TableCell>
                            <TableCell align="right">{formatCurrency(item.saldoHab)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
                              {formatCurrency(saldoAcumulado)}
                            </TableCell>
                            <TableCell
                              sx={
                                item.fechaVenc && new Date(item.fechaVenc) < new Date()
                                  ? { color: '#d32f2f', fontWeight: 600 }
                                  : {}
                              }
                            >
                              {item.fechaVenc ? formatterFecha.format(new Date(item.fechaVenc)) : '-'}
                            </TableCell>
                            <TableCell align="center">
                              {renderAccionesComposicion(item)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[10, 20, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={pageComposicion}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Filas por página"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    sx={{
                      borderTop: '1px solid #e0e0e0',
                    }}
                  />
                </>
              )}
            </TableContainer>
          )}
          </>
          )}

          {/* Tab 1: Movimientos Históricos */}
          {clienteInfo && currentTab === 1 && (
            <>
              <Box mb={2}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={2}
                >
                  <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<FilterListIcon />}
                    endIcon={showFiltrosMovimientos ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={() => setShowFiltrosMovimientos(!showFiltrosMovimientos)}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                  >
                    Filtrar
                  </Button>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      variant="outlined"
                      startIcon={<FileDownloadIcon />}
                      onClick={handleExportExcelMovimientos}
                      disabled={loadingMovimientos || exportingExcelMovimientos || movimientosHistoricos.length === 0}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                    >
                      {exportingExcelMovimientos ? 'Exportando...' : 'Exportar Excel'}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={handleExportPDFMovimientos}
                      disabled={loadingMovimientos || exportingPDFMovimientos || movimientosHistoricos.length === 0}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)'
                        }
                      }}
                    >
                      {exportingPDFMovimientos ? 'Generando...' : 'Exportar PDF'}
                    </Button>
                  </Stack>
                </Box>
                <Collapse in={showFiltrosMovimientos} timeout="auto" unmountOnExit>
                  <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" sx={{ mt: 2 }}>
                    <TextField
                        size="small"
                        label="Fecha Desde"
                        type="date"
                        value={fechaDesde}
                        onChange={(e) => setFechaDesde(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: { xs: '100%', sm: 150 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                      <TextField
                        size="small"
                        label="Fecha Hasta"
                        type="date"
                        value={fechaHasta}
                        onChange={(e) => setFechaHasta(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: { xs: '100%', sm: 150 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                      <TextField
                        size="small"
                        label="Buscar"
                        placeholder="Descripción, comprobante..."
                        value={searchTermMovimientos}
                        onChange={(e) => setSearchTermMovimientos(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter') handleBuscarMovimientos(); }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon color="action" fontSize="small" />
                            </InputAdornment>
                          )
                        }}
                        sx={{ width: { xs: '100%', sm: 220 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        size="medium"
                        onClick={handleBuscarMovimientos}
                        sx={{ borderRadius: 2, textTransform: 'none', width: { xs: '100%', sm: 'auto' } }}
                      >
                        Buscar
                      </Button>
                  </Box>
                </Collapse>
              </Box>

              {isMobile ? (
                <Box>
                  {renderCardsMovimientosMobile()}
                  {movimientosHistoricos.length > 0 && (
                    <TablePagination
                      rowsPerPageOptions={[10, 20, 50]}
                      component="div"
                      count={totalCountMovimientos}
                      rowsPerPage={rowsPerPageMovimientos}
                      page={pageMovimientos}
                      onPageChange={handleChangePageMovimientos}
                      onRowsPerPageChange={handleChangeRowsPerPageMovimientos}
                      labelRowsPerPage="Filas por página"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                      sx={{
                        mt: 2,
                        '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', gap: 1, px: 1 }
                      }}
                    />
                  )}
                </Box>
              ) : (
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 2,
                  boxShadow: 'none',
                  border: '1px solid #e0e0e0'
                }}
              >
                {loadingMovimientos ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={200} p={3}>
                    <CircularProgress />
                  </Box>
                ) : movimientosHistoricos.length === 0 ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={200} p={3}>
                    <Typography>No hay movimientos para este cliente con los filtros aplicados.</Typography>
                  </Box>
                ) : (
                  <>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Fecha Emisión</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Comprobante</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Número</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Importe Total</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Debe</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Haber</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Saldo Acumulado</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Fecha Venc.</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {mostrarSaldoInicialHistorico && (
                          <TableRow sx={{ bgcolor: '#f0f7ff', '&:hover': { bgcolor: '#e8f0fe' } }}>
                            <TableCell colSpan={4} sx={{ fontStyle: 'italic' }}>
                              Saldo Inicial al {formatFechaSoloDia(fechaDesde)}
                            </TableCell>
                            <TableCell align="right">-</TableCell>
                            <TableCell align="right">-</TableCell>
                            <TableCell align="right">-</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
                              {formatCurrency(saldoInicialPeriodo)}
                            </TableCell>
                            <TableCell>-</TableCell>
                            <TableCell>-</TableCell>
                            <TableCell align="center">-</TableCell>
                          </TableRow>
                        )}
                        {movimientosHistoricos.map((item, index) => {
                          const saldoAcumulado = calcularSaldoAcumuladoMovimientos(index);
                          const nroTipo = (item.nroTipoFormulario ?? '').toString().trim();
                          const descripcionTipTxt = (item.descripcionTip ?? '').toString().toUpperCase();
                          const esAnulado = esComprobanteAnulado(item.nombre, descripcionTipTxt);
                          const esRecibo =
                            nroTipo === '4' || descripcionTipTxt.includes('RECIBO');
                          return (
                          <TableRow
                            key={`${item.numero}-${item.fechaEmision}-${index}`}
                            sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}
                          >
                            <TableCell>
                              {item.fechaEmision ? formatterFecha.format(new Date(item.fechaEmision)) : '-'}
                            </TableCell>
                            <TableCell>{item.descripcionTip || '-'}</TableCell>
                            <TableCell>{numeroSinPrefijo(item.numero)}</TableCell>
                            <TableCell sx={{ maxWidth: 200 }}>{item.descripcionFor || '-'}</TableCell>
                            <TableCell align="right">{formatCurrency(item.importeTotal ?? 0)}</TableCell>
                            <TableCell align="right">{formatCurrency(item.impCompDebe ?? 0)}</TableCell>
                            <TableCell align="right">{formatCurrency(item.impCompHaber ?? 0)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
                              {formatCurrency(saldoAcumulado)}
                            </TableCell>
                            <TableCell
                              sx={
                                item.fechaVencim && new Date(item.fechaVencim) < new Date()
                                  ? { color: '#d32f2f', fontWeight: 600 }
                                  : {}
                              }
                            >
                              {item.fechaVencim ? formatterFecha.format(new Date(item.fechaVencim)) : '-'}
                            </TableCell>
                            <TableCell>
                              {esAnulado && esRecibo ? (
                                <Chip
                                  label="Anulado"
                                  size="small"
                                  sx={{
                                    bgcolor: '#9e9e9e',
                                    color: '#fff',
                                    fontWeight: 500,
                                    borderRadius: 1.5,
                                    minWidth: 72
                                  }}
                                />
                              ) : (
                                '-'
                              )}
                            </TableCell>
                          <TableCell align="center">
                            {renderAccionesMovimiento(item)}
                          </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    <TablePagination
                      rowsPerPageOptions={[10, 20, 50]}
                      component="div"
                      count={totalCountMovimientos}
                      rowsPerPage={rowsPerPageMovimientos}
                      page={pageMovimientos}
                      onPageChange={handleChangePageMovimientos}
                      onRowsPerPageChange={handleChangeRowsPerPageMovimientos}
                      labelRowsPerPage="Filas por página"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                      sx={{ borderTop: '1px solid #e0e0e0' }}
                    />
                  </>
                )}
              </TableContainer>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <ClienteSearch
        open={openClienteSearch}
        onClose={() => setOpenClienteSearch(false)}
        onSelect={handleClienteSelect}
        excluirInactivos={false}
      />

      {pdfUrl && selectedReciboPdfId && (
        <PDFViewer
          pdfUrl={pdfUrl}
          onClose={handleClosePDF}
          title={
            selectedPdfDocType === 'factura'
              ? `Factura ${selectedReciboPdfId}`
              : selectedPdfDocType === 'devolucion'
                ? `Devolución ${selectedReciboPdfId}`
                : `Recibo ${selectedReciboPdfId}`
          }
          documentId={`${selectedReciboPdfId}`}
          documentType={selectedPdfDocType}
          downloadFileName={pdfFileName ?? undefined}
        />
      )}

      {clienteInfo && (
        <AplicarSaldoReciboDialog
          open={openAplicarSaldoDialog}
          onClose={handleCloseAplicarDialog}
          nroCliente={clienteInfo.nroCliente}
          nombreCliente={clienteInfo.nombre || `Cliente ${clienteInfo.nroCliente}`}
          reciboId={debitoIdVerAplicaciones != null ? undefined : reciboIdAplicar ?? undefined}
          nroFormularioDebitoCliente={debitoIdVerAplicaciones ?? undefined}
          nroTipoFormularioDebitoCliente={debitoTipoVerAplicaciones}
          numeroComprobanteCredito={numeroComprobanteCreditoAplicar}
          numeroComprobanteDebito={numeroComprobanteDebitoAplicar}
          selectorMode={reciboIdAplicar == null && debitoIdVerAplicaciones == null}
          onSuccess={() => {
            loadData(clienteInfo.nroCliente);
            if (currentTab === 1) loadMovimientosHistoricos();
          }}
        />
      )}
    </Box>
  );
};

export default ResumenCuenta;
